import React, { useEffect, useMemo, useState } from "react";
import api from "../../api";
import { useNavigate } from "react-router-dom";

const CreatePost = () => {
  const navigate = useNavigate();

  const [postData, setPostData] = useState({
    title: "",
    slug: "",
    description: "",
    image: null,
  });

  const [newGalleryFiles, setNewGalleryFiles] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const mainImagePreview = useMemo(() => {
    return postData.image ? URL.createObjectURL(postData.image) : null;
  }, [postData.image]);

  useEffect(() => {
    api.get("/categories")
      .then((response) => setAllCategories(response.data))
      .catch((err) => { console.error(err); setError("Erreur lors du chargement des catégories."); })
      .finally(() => setLoadingCategories(false));
  }, []);

  useEffect(() => {
    return () => { if (mainImagePreview) URL.revokeObjectURL(mainImagePreview); };
  }, [mainImagePreview]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setPostData((prev) => ({ ...prev, [name]: value }));
  };

  const handleMainImageChange = (e) => {
    const file = e.target.files[0];
    if (file) setPostData((prev) => ({ ...prev, image: file }));
  };

  const handleCategoryChange = (e) => {
    const categoryName = e.target.value;
    setSelectedCategories((prev) =>
      e.target.checked ? [...prev, categoryName] : prev.filter((name) => name !== categoryName)
    );
  };

  const handleNewGalleryChange = (e) => {
    const files = Array.from(e.target.files);
    setNewGalleryFiles((prev) => [...prev, ...files]);
  };

  const handleRemoveNewFile = (index) => {
    setNewGalleryFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const moveGalleryFile = (fromIndex, toIndex) => {
    setNewGalleryFiles((prev) => {
      const updated = [...prev];
      if (fromIndex < 0 || toIndex < 0 || fromIndex >= updated.length || toIndex >= updated.length) return updated;
      const [movedItem] = updated.splice(fromIndex, 1);
      updated.splice(toIndex, 0, movedItem);
      return updated;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError("");
    setSubmitting(true);

    const formData = new FormData();
    formData.append("Title", postData.title);
    formData.append("Slug", postData.slug);
    formData.append("Description", postData.description);
    if (postData.image) formData.append("Image", postData.image);
    selectedCategories.forEach((category) => formData.append("Categories", category));
    newGalleryFiles.forEach((file) => formData.append("PostImages", file));

    try {
      await api.post("/admin/posts", formData);
      navigate("/dashboard/posts");
    } catch (err) {
      console.error(err);
      if (err.response?.data?.message) setError(err.response.data.message);
      else if (err.response?.data?.errors) setError(Object.values(err.response.data.errors).flat().join(" "));
      else setError("Erreur lors de la création du post.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingCategories) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <p className="text-slate-100 text-xl font-semibold">Chargement...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 px-4 py-10 text-slate-100">
      <div className="mx-auto max-w-5xl">
        <section className="mb-8 overflow-hidden rounded-3xl bg-gradient-to-r from-slate-900 via-slate-800 to-green-900 text-white shadow-sm">
          <div className="p-8 md:p-10">
            <p className="mb-3 inline-flex rounded-full bg-white/10 px-3 py-1 text-sm font-medium text-green-100">
              Dashboard posts
            </p>
            <h1 className="mb-3 text-3xl font-bold md:text-4xl">Créer un post</h1>
            <p className="max-w-2xl text-sm leading-7 text-slate-200 md:text-base">
              Ajoutez un nouvel article avec son contenu, une image principale, une galerie ordonnée et les catégories associées.
            </p>
          </div>
        </section>

        <section className="rounded-2xl border border-slate-800 bg-slate-900 p-6 shadow-sm">
          {error && (
            <div className="mb-6 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">{error}</div>
          )}
          {submitting && (
            <div className="mb-6 flex items-center gap-3 rounded-xl border border-blue-500/30 bg-blue-500/10 px-4 py-3 text-sm font-medium text-blue-300">
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-blue-400 border-t-transparent"></div>
              <span>Création du post en cours...</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-8">
            <div className="rounded-2xl border border-slate-800 bg-slate-950 p-6">
              <h2 className="mb-5 text-lg font-semibold text-white">Contenu principal</h2>
              <div className="space-y-6">
                <div>
                  <label htmlFor="title" className="mb-2 block text-sm font-medium text-slate-200">Titre</label>
                  <input
                    type="text" id="title" name="title" value={postData.title}
                    onChange={handleChange} required disabled={submitting}
                    className="block w-full rounded-xl border border-slate-700 bg-slate-900 px-4 py-3 text-slate-100 placeholder:text-slate-500 focus:border-blue-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label htmlFor="slug" className="mb-2 block text-sm font-medium text-slate-200">Slug optionnel</label>
                  <input
                    type="text" id="slug" name="slug" value={postData.slug}
                    onChange={handleChange} disabled={submitting}
                    placeholder="Laisse vide pour générer automatiquement"
                    className="block w-full rounded-xl border border-slate-700 bg-slate-900 px-4 py-3 text-slate-100 placeholder:text-slate-500 focus:border-blue-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label htmlFor="description" className="mb-2 block text-sm font-medium text-slate-200">Contenu</label>
                  <textarea
                    id="description" name="description" value={postData.description}
                    onChange={handleChange} rows={8} required disabled={submitting}
                    className="block w-full rounded-xl border border-slate-700 bg-slate-900 px-4 py-3 text-slate-100 placeholder:text-slate-500 focus:border-blue-500 focus:outline-none"
                  />
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-800 bg-slate-950 p-6">
              <h2 className="mb-5 text-lg font-semibold text-white">Image principale</h2>
              <input
                id="main-image" type="file" onChange={handleMainImageChange}
                accept="image/*" disabled={submitting}
                className="block w-full rounded-xl border border-slate-700 bg-slate-900 px-4 py-3 text-slate-100 file:mr-4 file:rounded-lg file:border-0 file:bg-white file:px-4 file:py-2 file:text-sm file:font-medium file:text-slate-950 hover:file:bg-slate-200"
              />
              {mainImagePreview && (
                <div className="mt-5">
                  <p className="mb-3 text-sm font-medium text-slate-300">Aperçu</p>
                  <img src={mainImagePreview} alt="preview" className="h-40 rounded-xl border border-slate-700 object-cover shadow" />
                </div>
              )}
            </div>

            <div className="rounded-2xl border border-slate-800 bg-slate-950 p-6">
              <h2 className="mb-5 text-lg font-semibold text-white">Galerie</h2>
              <input
                id="gallery" type="file" multiple onChange={handleNewGalleryChange}
                accept="image/*" disabled={submitting}
                className="block w-full rounded-xl border border-slate-700 bg-slate-900 px-4 py-3 text-slate-100 file:mr-4 file:rounded-lg file:border-0 file:bg-white file:px-4 file:py-2 file:text-sm file:font-medium file:text-slate-950 hover:file:bg-slate-200"
              />
              {newGalleryFiles.length > 0 && (
                <>
                  <p className="mt-4 text-sm text-slate-400">Réorganise les images dans l'ordre souhaité.</p>
                  <div className="mt-5 flex flex-wrap gap-4">
                    {newGalleryFiles.map((file, index) => (
                      <div key={`${file.name}-${index}`} className="relative rounded-xl border border-slate-700 bg-slate-900 p-3">
                        <span className="absolute left-2 top-2 rounded-lg bg-black/70 px-2 py-1 text-xs font-medium text-white">{index + 1}</span>
                        <img src={URL.createObjectURL(file)} className="h-24 w-24 rounded-lg object-cover" alt="" />
                        <div className="mt-3 flex justify-center gap-2">
                          <button type="button" onClick={() => moveGalleryFile(index, index - 1)} disabled={submitting || index === 0}
                            className="rounded-lg bg-slate-700 px-2 py-1 text-xs font-medium text-white transition hover:bg-slate-600 disabled:opacity-50">←</button>
                          <button type="button" onClick={() => moveGalleryFile(index, index + 1)} disabled={submitting || index === newGalleryFiles.length - 1}
                            className="rounded-lg bg-slate-700 px-2 py-1 text-xs font-medium text-white transition hover:bg-slate-600 disabled:opacity-50">→</button>
                          <button type="button" onClick={() => handleRemoveNewFile(index)} disabled={submitting}
                            className="rounded-lg bg-red-500 px-2 py-1 text-xs font-medium text-white transition hover:bg-red-600">X</button>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            <div className="rounded-2xl border border-slate-800 bg-slate-950 p-6">
              <h2 className="mb-5 text-lg font-semibold text-white">Catégories</h2>
              <div className="flex flex-wrap gap-4">
                {allCategories.map((cat) => (
                  <label key={cat.id} className="inline-flex items-center gap-2 rounded-xl border border-slate-700 bg-slate-900 px-4 py-2 text-sm text-slate-300">
                    <input type="checkbox" className="accent-blue-500" value={cat.name}
                      checked={selectedCategories.includes(cat.name)} onChange={handleCategoryChange} disabled={submitting} />
                    <span>{cat.name}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row">
              <button type="submit" disabled={submitting}
                className="w-full rounded-xl bg-blue-600 px-6 py-3 font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto">
                {submitting ? "Création en cours..." : "Créer le post"}
              </button>
              <button type="button" onClick={() => navigate("/dashboard/posts")} disabled={submitting}
                className="w-full rounded-xl border border-slate-700 px-6 py-3 font-semibold text-slate-200 transition hover:bg-slate-800 disabled:opacity-60 sm:w-auto">
                Annuler
              </button>
            </div>
          </form>
        </section>
      </div>
    </div>
  );
};

export default CreatePost;